import {
  IGetWalletTransactionDetailsResponse,
  IWalletTransactionDetailsBreakdownApi,
  IWalletTransactionDetailsDataApi,
  IWalletTransactionDetailsOrderApi,
  IWalletTransactionDetailsTransactionApi,
  WalletTransactionDetailsBreakdown,
  WalletTransactionDetailsOrder,
  WalletTransactionDetailsTransaction,
  WalletTransactionDetailsViewModel
} from '@/ducks/billing/billing.types'

const toFiniteNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return fallback
}

const normalizeWalletSource = (
  walletSource?: string
): WalletTransactionDetailsViewModel['walletSource'] => {
  if (walletSource === 'store-wallet') return 'store-wallet'
  if (walletSource === 'fs') return 'fs'
  return 'unknown'
}

const normalizeTransaction = (
  transaction?: IWalletTransactionDetailsTransactionApi
): WalletTransactionDetailsTransaction => {
  const orderId = transaction?.order_id?.trim()
  const fundingAttemptId = transaction?.funding_attempt_id?.trim()

  return {
    transactionId: String(transaction?.transaction_id || ''),
    status: String(transaction?.status || ''),
    transactionType: String(transaction?.transaction_type || ''),
    transactionDateTime: String(transaction?.transaction_date_time || ''),
    currency: String(transaction?.currency || ''),
    ...(orderId ? { orderId } : {}),
    ...(fundingAttemptId ? { fundingAttemptId } : {}),
    ...(transaction?.cash_out_display_status
      ? { cashOutDisplayStatus: transaction.cash_out_display_status }
      : {}),
    ...(transaction?.next_action ? { nextAction: transaction.next_action } : {})
  }
}

const normalizeOrderStatusTitle = (title?: string | Record<string, string>) => {
  if (typeof title === 'string') {
    return { title, titleI18n: undefined }
  }

  if (title && typeof title === 'object') {
    const titleI18n = title
    const fallbackTitle =
      titleI18n.en ||
      titleI18n.fr ||
      titleI18n.ar ||
      Object.values(titleI18n).find(value => typeof value === 'string') ||
      ''

    return {
      title: fallbackTitle,
      titleI18n
    }
  }

  return { title: '', titleI18n: undefined }
}

const normalizeOrder = (
  order?: IWalletTransactionDetailsOrderApi | null
): WalletTransactionDetailsOrder | null => {
  if (!order?.order_id) return null

  const { title, titleI18n } = normalizeOrderStatusTitle(order.status?.title)

  return {
    orderId: String(order.order_id),
    customerName: String(order.customer_name || ''),
    status: {
      key: String(order.status?.key || ''),
      title,
      ...(titleI18n ? { titleI18n } : {})
    }
  }
}

const normalizeBreakdown = (
  breakdown?: IWalletTransactionDetailsBreakdownApi | null
): WalletTransactionDetailsBreakdown | null => {
  if (!breakdown) return null

  const paymentMethod = breakdown.payment_method || {}

  return {
    subtotal: toFiniteNumber(breakdown.subtotal),
    commission: toFiniteNumber(breakdown.commission),
    discount: toFiniteNumber(breakdown.discount),
    earnings: toFiniteNumber(breakdown.earnings),
    paymentMethod: {
      displayName: String(paymentMethod.display_name || ''),
      ...(paymentMethod.icon ? { icon: paymentMethod.icon } : {}),
      ...(paymentMethod.display_name_i18n
        ? { displayNameI18n: paymentMethod.display_name_i18n }
        : {})
    }
  }
}

export const normalizeWalletTransactionDetailsData = (
  data?: IWalletTransactionDetailsDataApi
): WalletTransactionDetailsViewModel => {
  return {
    walletSource: 'unknown',
    transaction: normalizeTransaction(data?.transaction),
    order: normalizeOrder(data?.order),
    breakdown: normalizeBreakdown(data?.breakdown)
  }
}

export const normalizeWalletTransactionDetailsResponse = (
  response: IGetWalletTransactionDetailsResponse
): WalletTransactionDetailsViewModel => {
  const normalized = normalizeWalletTransactionDetailsData(response.data)

  return {
    ...normalized,
    walletSource: normalizeWalletSource(response.walletSource)
  }
}
